"use client";

import React, { useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";
import { Select, SelectItem } from "@/components/ui/simple-select";
import {
  OFFERING_ROLES,
  HARD_COST_CATEGORIES,
} from "@/lib/types/database/offerings";
import type {
  StaffLineItem,
  HardCostLineItem,
  OfferingRoleId,
  HardCostCategoryId,
} from "@/lib/types/database/offerings";
import { FieldWrapper } from "./context";
import type { FieldRenderContext } from "./context";

type LineItemsVariant = "staff" | "hard-cost";

interface OfferingLineItemsFormFieldProps {
  ctx: FieldRenderContext;
  variant: LineItemsVariant;
}

function StaffLineItemRow({
  item,
  onUpdate,
  onRemove,
}: {
  item: StaffLineItem;
  onUpdate: (u: Partial<StaffLineItem>) => void;
  onRemove: () => void;
}) {
  const roleDef = OFFERING_ROLES.find((r) => r.id === item.role);
  const displayRate = item.rate ?? roleDef?.defaultRate ?? 0;

  return (
    <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/50 border rounded-lg">
      <Select
        value={item.role}
        onValueChange={(v) => onUpdate({ role: v as OfferingRoleId })}
        className="w-[180px] shrink-0"
      >
        {OFFERING_ROLES.map((r) => (
          <SelectItem key={r.id} value={r.id}>
            {r.label} (${r.defaultRate}/hr)
          </SelectItem>
        ))}
      </Select>
      <Input
        type="number"
        min={0}
        step={0.5}
        placeholder="Hours"
        value={item.hours || ""}
        onChange={(e) => onUpdate({ hours: Number(e.target.value) || 0 })}
        className="w-20 shrink-0"
      />
      <Input
        type="number"
        min={0}
        step={1}
        placeholder={`Rate (default ${roleDef?.defaultRate ?? 0})`}
        value={item.rate ?? ""}
        onChange={(e) => {
          const v = e.target.value;
          onUpdate({ rate: v === "" ? undefined : Number(v) });
        }}
        className="w-24 shrink-0"
      />
      <span className="text-sm text-muted-foreground shrink-0">
        = ${(displayRate * item.hours).toFixed(2)}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="h-8 w-8 p-0 text-destructive shrink-0"
        title="Remove"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function HardCostLineItemRow({
  item,
  onUpdate,
  onRemove,
}: {
  item: HardCostLineItem;
  onUpdate: (u: Partial<HardCostLineItem>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/50 border rounded-lg">
      <Select
        value={item.description}
        onValueChange={(v) => onUpdate({ description: v as HardCostCategoryId })}
        className="w-[180px] shrink-0"
      >
        {HARD_COST_CATEGORIES.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.label}
          </SelectItem>
        ))}
      </Select>
      <Input
        type="number"
        min={0}
        step={0.01}
        placeholder="Amount"
        value={item.amount ?? ""}
        onChange={(e) => onUpdate({ amount: Number(e.target.value) || 0 })}
        className="w-24 shrink-0"
      />
      <span className="text-sm text-muted-foreground shrink-0">
        ${(item.amount ?? 0).toFixed(2)}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="h-8 w-8 p-0 text-destructive shrink-0"
        title="Remove"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function OfferingLineItemsFormField({ ctx, variant }: OfferingLineItemsFormFieldProps) {
  const { fieldName, field, setValue, watchedValues, errors } = ctx;
  const hasError = !!errors[fieldName];
  const errorMessage = errors[fieldName]?.message as string;

  const rawValue = watchedValues[fieldName];
  const items = useMemo(() => {
    if (!Array.isArray(rawValue)) return [];
    if (variant === "staff") {
      return rawValue.filter(
        (x): x is StaffLineItem =>
          x && typeof x === "object" && typeof (x as StaffLineItem).role === "string" && typeof (x as StaffLineItem).hours === "number"
      );
    }
    return rawValue.filter(
      (x): x is HardCostLineItem =>
        x && typeof x === "object" && typeof (x as HardCostLineItem).description === "string" && typeof (x as HardCostLineItem).amount === "number"
    );
  }, [rawValue, variant]);

  const handleAdd = useCallback(() => {
    if (variant === "staff") {
      setValue(fieldName, [...items, { role: "engineer" as OfferingRoleId, hours: 0, rate: undefined }], {
        shouldValidate: true,
      });
    } else {
      setValue(fieldName, [...items, { description: "other" as HardCostCategoryId, amount: 0 }], {
        shouldValidate: true,
      });
    }
  }, [fieldName, items, variant, setValue]);

  const handleUpdate = useCallback(
    (index: number, updates: Partial<StaffLineItem> | Partial<HardCostLineItem>) => {
      const next = [...items];
      if (variant === "staff") {
        (next as StaffLineItem[])[index] = { ...(next[index] as StaffLineItem), ...(updates as Partial<StaffLineItem>) };
      } else {
        (next as HardCostLineItem[])[index] = { ...(next[index] as HardCostLineItem), ...(updates as Partial<HardCostLineItem>) };
      }
      setValue(fieldName, next, { shouldValidate: true });
    },
    [fieldName, items, variant, setValue]
  );

  const handleRemove = useCallback(
    (index: number) => {
      const next = items.filter((_, i) => i !== index);
      setValue(fieldName, next, { shouldValidate: true });
    },
    [fieldName, items, setValue]
  );

  const label = variant === "staff" ? "Staff Line Items" : "Hard Cost Line Items";
  const addLabel = variant === "staff" ? "Add Staff" : "Add Hard Cost";

  return (
    <FieldWrapper
      fieldName={fieldName}
      field={field}
      hasError={hasError}
      errorMessage={errorMessage || (variant === "staff" ? "Add at least one staff role" : "Add at least one hard cost item")}
    >
      <div className="space-y-3">
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground italic p-4 bg-muted/30 rounded-lg border border-dashed">
              No {label.toLowerCase()} yet. Click &quot;{addLabel}&quot; to add.
            </div>
          ) : (
            items.map((item, index) =>
              variant === "staff" ? (
                <StaffLineItemRow
                  key={index}
                  item={item as StaffLineItem}
                  onUpdate={(u) => handleUpdate(index, u)}
                  onRemove={() => handleRemove(index)}
                />
              ) : (
                <HardCostLineItemRow
                  key={index}
                  item={item as HardCostLineItem}
                  onUpdate={(u) => handleUpdate(index, u)}
                  onRemove={() => handleRemove(index)}
                />
              )
            )
          )}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={handleAdd} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          {addLabel}
        </Button>
      </div>
    </FieldWrapper>
  );
}
